// InventoryUsagePanel — Phase 5b-2 inventory-usage endpoint.
// 시안 design/v3/AnalyticsPanels.jsx:413-449 의 물품 재고 layout 따름.
// endpoint 는 boxhero out/sale_out 합산 — quantity 만 제공.
// (시안의 status critical/low/ok·current/min/turnover 는 별도 phase 6 에서 inventory 와 통합).

import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHead,
  ShareBar,
} from '../bits';
import { useInventoryUsage, type AnalyticsScope } from '../queries';
import { fmtNum } from '../../../lib/format';

interface Props {
  scope: AnalyticsScope;
}

export function InventoryUsagePanel({ scope }: Props) {
  const q = useInventoryUsage(scope);
  const items = q.data ?? [];
  const maxQty = Math.max(1, ...items.map((i) => i.quantity));

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead
        title="물품 재고 현황"
        sub={items.length ? `${items.length}건 · 사용량 합` : '데이터 없음'}
      />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && items.length === 0 && (
        <EmptyState
          title="재고 사용 내역이 없습니다"
          message="BoxHero out/sale_out tx 가 기간 내에 없습니다."
        />
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map((it) => (
            <div
              key={it.itemName}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.4fr 0.6fr',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderBottom: '1px solid var(--border-weak)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: 'var(--primary-strong)',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--fg-1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {it.itemName}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ShareBar value={it.quantity} max={maxQty} />
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                  textAlign: 'right',
                }}
              >
                {fmtNum(it.quantity)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
